import CompanyWebFactSections from "@/components/company/company-web-fact-sections";
import CompanyWebFigures, {
  CompanyWebFiguresSkeleton,
} from "@/components/company/company-web-figures";
import CompanyWebHighlights from "@/components/company/company-web-highlights";
import CompanyWebLegalSection from "@/components/company/company-web-legal-section";
import CompanyWebProgressCard from "@/components/company/company-web-progress-card";
import CompanyActivityBox from "@/components/company/shared/company-activity-box";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import SourceHostLink from "@/components/company/shared/source-host-link";
import BlurInText from "@/components/ui/blur-in-text";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import { groupFactsBySection } from "@/lib/company-web-facts";
import { companyWebLegalRows } from "@/lib/company-web-legal";
import { convertDateToDistanceNow } from "@/lib/date";
import { sourceLink } from "@/lib/source-link";
import { cn } from "@/lib/utils";
import {
  hasWebProfileNotice,
  isRetryableFailure,
  isRunInProgress,
  webSynthesisOf,
  type CompanyWebPreviewState,
} from "@/stores/company-web-preview-store";
import type {
  CompanyDTO,
  CompanyTag,
  CompanyWebCandidate,
  CompanyWebFact,
  CompanyWebKeyData,
  CompanyWebProfileDTO,
  CompanyWebProfilePartialDTO,
} from "@shaarp/common";
import { GlobeIcon, RotateCwIcon } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { useState, type ReactNode } from "react";

/** The message of a request that failed, by the code it failed with. */
function errorMessageKey(code: string | undefined): string {
  switch (code) {
    case "invalid_tag":
      return "company.webPreview.error.invalid_tag";
    case "rate_limited":
      return "company.webPreview.error.rate_limited";
    default:
      return "company.webPreview.error.unavailable";
  }
}

/** Why there is no profile to show, with a retry when asking again may help. */
function ProfileNotice({
  children,
  onRetry,
}: {
  children: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {children}
      </p>
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="max-sm:min-h-11"
        >
          <RotateCwIcon />
          {t("company.webPreview.retry")}
        </Button>
      )}
    </div>
  );
}

export function SynthesisText({
  text,
  isWriting = false,
  className,
}: {
  text: string;
  isWriting?: boolean;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "whitespace-pre-line break-words text-sm leading-relaxed",
        className,
      )}
    >
      <BlurInText text={text} isWriting={isWriting} />
    </p>
  );
}

/** The identifiers a mention carries, shown in the legal section whatever the web found. */
type TagIdentity = Pick<CompanyTag, "siren" | "siret" | "address" | "city">;

/**
 * The fact sections, and the legal section in the place of the legal facts:
 * after the details of the tag, the legal identity as label/value rows with
 * what happened to the legal structure below them.
 */
function FactsWithLegal({
  facts,
  keyData,
  tag,
  company,
  afterSynthesis,
  isWriting = false,
}: {
  facts: CompanyWebFact[];
  keyData?: Partial<CompanyWebKeyData>;
  tag?: TagIdentity | null;
  company?: CompanyDTO | null;
  afterSynthesis?: ReactNode;
  isWriting?: boolean;
}) {
  const sections = groupFactsBySection(facts);
  const legal = sections.find((section) => section.id === "legal");
  return (
    <>
      {afterSynthesis}
      <CompanyWebLegalSection
        rows={companyWebLegalRows({ keyData, tag, company })}
        facts={legal?.facts}
        isWriting={isWriting}
      />
      <CompanyWebFactSections
        sections={sections.filter((section) => section.id !== "legal")}
        isWriting={isWriting}
      />
    </>
  );
}

/** "Web synthesis" header: what follows is what public pages say. */
function SynthesisSection({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section>
      <SectionHeaderWithIcon
        icon={GlobeIcon}
        title={t("company.webPreview.title")}
      />
      {children}
    </section>
  );
}

/** The namesakes the web run could not tell apart, read-only. */
function HomonymCandidates({
  candidates,
  isWriting = false,
}: {
  candidates: CompanyWebCandidate[];
  isWriting?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed">{t("company.webPreview.homonyms")}</p>
      {/* Read-only: nothing here picks a candidate yet. */}
      <ul className="flex flex-col gap-2">
        {candidates.map((candidate) => {
          const place = [candidate.city, candidate.address]
            .filter(Boolean)
            .join(" · ");
          const source = sourceLink(candidate.url);
          return (
            <li
              key={`${candidate.name}|${candidate.url}`}
              className={cn(
                "min-w-0 rounded-xl border border-border bg-muted/30 px-3 py-2.5",
                isWriting && "blur-in-appear",
              )}
            >
              <p className="break-words text-sm font-semibold">{candidate.name}</p>
              {place && (
                <p className="break-words text-sm text-muted-foreground">
                  {place}
                </p>
              )}
              {source && (
                <SourceHostLink link={source} className="inline-flex max-w-full" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Why a settled web run shows nothing on a page that keeps its own layout
 * (the company page, desktop and mobile): no profile found, a failure — with
 * a retry when asking again may help —, or the namesakes it could not tell
 * apart. Nothing while the run goes on or when it wrote a profile.
 */
export function CompanyWebProfileNotice({
  state,
  onRetry,
}: {
  state: CompanyWebPreviewState;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const retry = isRetryableFailure(state) ? onRetry : undefined;

  if (!hasWebProfileNotice(state)) return null;
  if (state.phase === "error" || !state.profile) {
    return (
      <ProfileNotice onRetry={retry}>
        {t(errorMessageKey(state.errorCode))}
      </ProfileNotice>
    );
  }
  switch (state.profile.status) {
    case "none":
      return (
        <ProfileNotice onRetry={retry}>{t("company.webPreview.nothing")}</ProfileNotice>
      );
    case "error":
      return (
        <ProfileNotice onRetry={retry}>
          {t("company.webPreview.error.unavailable")}
        </ProfileNotice>
      );
    case "homonyms":
      return <HomonymCandidates candidates={state.profile.candidates} />;
    default:
      return null;
  }
}

/** When the profile was built, from how many facts and sources. */
function ProfileStatsFooter({ profile }: { profile: CompanyWebProfileDTO }) {
  const { t, currentLanguage } = useTranslation();
  const when = convertDateToDistanceNow(new Date(profile.createdAt), {
    languageOverride: currentLanguage,
  });
  return (
    <p className="text-xs tabular-nums text-muted-foreground">
      {t("company.webPreview.footer", {
        when,
        facts: t("company.webPreview.factsCount", {
          count: profile.stats.factsCount,
        }),
        sources: t("company.webPreview.sourcesCount", {
          count: profile.stats.sourcesCount,
        }),
      })}
    </p>
  );
}

/**
 * The footer of a settled profile, for the panel of a company in base to put
 * under its own details: nothing while the run goes or when nothing was found.
 */
export function CompanyWebProfileFooter({
  state,
}: {
  state: CompanyWebPreviewState;
}) {
  const { profile } = state;
  if (state.phase !== "done" || profile?.status !== "ok") return null;
  return <ProfileStatsFooter profile={profile} />;
}

/** Placeholder lines while the synthesis is not written yet. */
export function SynthesisSkeleton() {
  return (
    <div className="space-y-2" aria-hidden>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * The synthesis, as the header's summary of a company panel or page: written,
 * being written, or awaited until the model says it found one company (or
 * homonyms, or nothing).
 */
export function CompanyWebSynthesisBox({
  state,
}: {
  state: CompanyWebPreviewState;
}) {
  const { t } = useTranslation();
  const { synthesis, isWriting, awaited } = webSynthesisOf(state);
  if (!synthesis && !awaited) return null;

  return (
    <CompanyActivityBox title={t("company.webPreview.title")}>
      {synthesis ? (
        <SynthesisText text={synthesis} isWriting={isWriting} />
      ) : (
        <SynthesisSkeleton />
      )}
    </CompanyActivityBox>
  );
}

/**
 * The profile as the model writes it, in the layout of the finished one: each
 * part blurs in as soon as it is written — the synthesis a word at a time —
 * and the parts still to come keep their placeholder. Figures, takeaways and
 * facts wait for the model to say it identified the company; homonyms show
 * their candidates instead.
 */
function StreamingProfile({
  partial,
  company,
  tag,
  showSynthesis,
  showHighlights,
  afterSynthesis,
}: {
  partial: CompanyWebProfilePartialDTO | undefined;
  company?: CompanyDTO | null;
  tag?: TagIdentity | null;
  showSynthesis: boolean;
  showHighlights: boolean;
  afterSynthesis?: ReactNode;
}) {
  const isOk = partial?.status === "ok";

  if (partial?.status === "homonyms") {
    return (
      <>
        {partial.candidates.length > 0 ? (
          <HomonymCandidates candidates={partial.candidates} isWriting />
        ) : (
          <SynthesisSkeleton />
        )}
        <FactsWithLegal
          facts={[]}
          tag={tag}
          company={company}
          afterSynthesis={afterSynthesis}
        />
      </>
    );
  }

  return (
    <>
      {/* A company in base shows the web's figures among its own tiles. */}
      {!company &&
        (isOk ? (
          <CompanyWebFigures keyData={partial.keyData} tag={tag} isWriting />
        ) : (
          <CompanyWebFiguresSkeleton />
        ))}
      {showHighlights && isOk && (
        <CompanyWebHighlights highlights={partial.highlights} isWriting />
      )}
      {showSynthesis && (
        <SynthesisSection>
          {partial?.synthesis ? (
            <SynthesisText text={partial.synthesis} isWriting />
          ) : (
            <SynthesisSkeleton />
          )}
        </SynthesisSection>
      )}
      <FactsWithLegal
        facts={isOk ? partial.facts : []}
        keyData={isOk ? partial.keyData : undefined}
        tag={tag}
        company={company}
        afterSynthesis={afterSynthesis}
        isWriting
      />
    </>
  );
}

type CompanyWebProfileViewProps = {
  state: CompanyWebPreviewState;
  company?: CompanyDTO | null;
  tag?: TagIdentity | null;
  showSynthesis?: boolean;
  showHighlights?: boolean;
  afterSynthesis?: ReactNode;
  onRetry?: () => void;
};

/**
 * Everything the web run adds to a panel, below the header: the progress card
 * while it runs, above the profile filling in as it is written, then key
 * figures, takeaways, the synthesis, the legal identity, the facts by category
 * and the footer. `afterSynthesis` lands between the synthesis and the legal
 * section.
 *
 * `showSynthesis` is off where the panel header already carries the synthesis,
 * `showHighlights` where another card carries the takeaways.
 * The identifiers of `tag` fill the legal section whatever the web found. With
 * `company` (already in base) the figures are left to the base panel's tiles
 * (`webFigureTiles`) and the footer to the end of its body
 * (`CompanyWebProfileFooter`), and the base's legal rows win, so only the ones
 * it lacks are shown. `onRetry` puts a retry button under a failure that asking again
 * may fix.
 */
export default function CompanyWebProfileView(props: CompanyWebProfileViewProps) {
  const { state } = props;
  const running = isRunInProgress(state);
  // One card per run: a run starting while the last card still blurs out (the
  // next company, a retry) gets a fresh card instead of reviving the fading one.
  const [run, setRun] = useState(0);
  const [wasRunning, setWasRunning] = useState(running);
  if (running !== wasRunning) {
    setWasRunning(running);
    if (running) setRun(run + 1);
  }

  return (
    <>
      {/* Outside the phase switch below, so the card outlives the run long
          enough to blur out once the profile is written. */}
      <AnimatePresence initial={false}>
        {running && (
          <CompanyWebProgressCard
            key={run}
            progress={state.progress}
            writing={state.phase === "streaming"}
          />
        )}
      </AnimatePresence>
      <WebProfile {...props} />
    </>
  );
}

/** The profile at the current phase of the run, without the progress card. */
function WebProfile({
  state,
  company,
  tag,
  showSynthesis = true,
  showHighlights = true,
  afterSynthesis,
  onRetry,
}: CompanyWebProfileViewProps) {
  const { t } = useTranslation();
  const { profile } = state;
  const retry = isRetryableFailure(state) ? onRetry : undefined;
  // What the tag itself says stays visible whatever the web run found.
  const withTail = (body: ReactNode) => (
    <>
      {body}
      <FactsWithLegal
        facts={[]}
        tag={tag}
        company={company}
        afterSynthesis={afterSynthesis}
      />
    </>
  );

  if (isRunInProgress(state)) {
    return (
      <StreamingProfile
        partial={state.partial}
        company={company}
        tag={tag}
        showSynthesis={showSynthesis}
        showHighlights={showHighlights}
        afterSynthesis={afterSynthesis}
      />
    );
  }

  if (state.phase === "done" && profile) {
    if (profile.status === "none") {
      return withTail(
        <ProfileNotice onRetry={retry}>{t("company.webPreview.nothing")}</ProfileNotice>,
      );
    }
    if (profile.status === "error") {
      return withTail(
        <ProfileNotice onRetry={retry}>
          {t("company.webPreview.error.unavailable")}
        </ProfileNotice>,
      );
    }
    if (profile.status === "homonyms") {
      return withTail(<HomonymCandidates candidates={profile.candidates} />);
    }

    return (
      <>
        {!company && <CompanyWebFigures keyData={profile.keyData} tag={tag} />}
        {showHighlights && (
          <CompanyWebHighlights
            highlights={profile.highlights}
            sources={profile.sources}
          />
        )}
        {showSynthesis && profile.synthesis && (
          <SynthesisSection>
            <SynthesisText text={profile.synthesis} />
          </SynthesisSection>
        )}
        <FactsWithLegal
          facts={profile.facts}
          keyData={profile.keyData}
          tag={tag}
          company={company}
          afterSynthesis={afterSynthesis}
        />
        {!company && <ProfileStatsFooter profile={profile} />}
      </>
    );
  }

  return withTail(
    <ProfileNotice onRetry={retry}>{t(errorMessageKey(state.errorCode))}</ProfileNotice>,
  );
}
